import { RiskRecognitionsRequester } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/RiskRecognitionsRequester';
import type { SilenceReason } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Environment } from '@cobliteam/react-environments';
import { useMutation } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues.
// `apiHost`, not `herbieApiHost`: this endpoint has no /herbie-1.1 path prefix.
const getRiskRecognitionsRequester = () => new RiskRecognitionsRequester({ host: env.apiHost });

interface CreateSilenceVariables {
  detectionId: string;
  reason: SilenceReason;
}

/**
 * Records a manager action on a highlighted detection (contacted / dismissed).
 *
 * Fire-and-forget by design: the card is hidden optimistically in the UI, so a
 * failed silence triggers no rollback and no refetch — the server re-surfaces the
 * driver on the next ~10-min window if the silence didn't land. `onError` only
 * logs (the request is also auto-reported to Datadog by CobliAxios), so a missed
 * silence is greppable rather than silent.
 */
const useCreateRiskRecognitionSilence = () =>
  useMutation<void, Error, CreateSilenceVariables>({
    mutationFn: ({ detectionId, reason }) => getRiskRecognitionsRequester().createSilence(detectionId, reason),
    onError: (error, { detectionId, reason }) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      console.error('[risk-recognition] failed to create silence', { detectionId, reason, status, error });
    },
  });

export { useCreateRiskRecognitionSilence };
